import { format } from "date-fns";
import { it } from 'date-fns/locale/it';
import { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from '../../lib/redux/hooks';
import { CustomAccordion } from "../../ui/molecules/accordion";
import { Calendar } from "../../ui/molecules/calendar";
import InputMultiSearch from "../../ui/molecules/input/InputMultiSearch";
import { MadfarmInput } from '../../ui/molecules/input/newInput';
import { CustomRadio } from "../../ui/molecules/radioButton";
import { MadfarmStaticSelect } from '../../ui/molecules/select/newSelect/static';
import TextArea from "../../ui/molecules/textArea/TextArea";
import { OptionType, Province, Region, formaGiuridica, regions } from "../../utils";
import { capComuneMap } from "../../utils/capComuniMap";
import { provinceComuniMap } from "../../utils/listaComuni";
import { GetAllOdc, resetGetAllOdcStatus } from "../odc/slice";
import { GetCustomerByCodeDTO } from "./dto";
import { resetCustomerCreateError, setCustomerCreateRequestField } from "./feature/slice";
import { GetCustomersByCode, setEntryOdc } from "./slice";
import './style.scss';

registerLocale('it', it)

export function BoxAnagrafica() {
    const customerState = useAppSelector(state => state.customer)
    const odcState = useAppSelector(state => state.odc)
    const dispatch = useAppDispatch()

    const [expanded, setExpanded] = useState<string | false>("panel1");
    const [provinceOptions, setProvinceOptions] = useState<OptionType[]>([]);
    const [rpProvinceOptions, setRpProvinceOptions] = useState<OptionType[]>([]);
    const [comuneOptions, setComuneOptions] = useState<OptionType[]>([]);
    const [rpComuneOptions, setRpComuneOptions] = useState<OptionType[]>([]);
    const [odc, setOdc] = useState<OptionType[]>([])

    const [primaNotifica, setPrimaNotifica] = useState<string | null>(null);
    const [subentro, setSubentro] = useState<string | null>(null);

    const regionOptions: OptionType[] = regions.map((region: Region) => ({
        value: region.name,
        label: region.name
    }));

    const handleToggleAccordion = (panel: string) => {
        setExpanded(expanded === panel ? false : panel);
    };

    useEffect(() => {
        dispatch(GetAllOdc({ itemsPerPage: 0, page: 0 }))
    }, [dispatch])

    useEffect(() => {
        if (customerState.customerCreateRequest.countryRegion) {
            const foundRegion = regions.find((r) => r.name === customerState.customerCreateRequest.countryRegion);
            if (foundRegion) {
                setProvinceOptions(foundRegion.provinces.map(p => ({ value: p.code, label: p.name })));
            }
        }
    }, []);

    useEffect(() => {
        if (odcState.getAllOdcStatus === 'successfully' && odcState.getAllOdcResponse !== undefined) {
            dispatch(resetGetAllOdcStatus())
            const data = odcState.getAllOdcResponse.data.map(odc => ({
                value: odc.id,
                label: odc.companyName
            } as OptionType))
            setOdc(data)
        }
    }, [odcState.getAllOdcStatus, odcState.getAllOdcResponse, dispatch])

    const handleRegionChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerCreateRequestField({ field: 'countryRegion', value }));
        dispatch(setCustomerCreateRequestField({ field: 'province', value: null }));
        dispatch(setCustomerCreateRequestField({ field: 'city', value: null }));

        if (value) {
            const foundRegion = regions.find(r => r.name === value);
            setProvinceOptions(foundRegion ? foundRegion.provinces.map(p => ({ value: p.code, label: p.name })) : []);
        } else {
            setProvinceOptions([]);
        }
        setComuneOptions([]);
    };

    const handleProvinceChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerCreateRequestField({ field: 'province', value }));
        dispatch(setCustomerCreateRequestField({ field: 'city', value: null }));

        if (value) {
            const comuni = provinceComuniMap.get(value) || [];
            setComuneOptions(comuni.map(c => ({ value: c, label: c })));
        } else {
            setComuneOptions([]);
        }
    };

    const handleRpRegionChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerCreateRequestField({ field: 'rpCountryRegion', value }));
        dispatch(setCustomerCreateRequestField({ field: 'rpProvince', value: null }));
        dispatch(setCustomerCreateRequestField({ field: 'rpCity', value: null }));

        if (value) {
            const foundRegion = regions.find(r => r.name === value);
            setRpProvinceOptions(foundRegion ? foundRegion.provinces.map(p => ({ value: p.code, label: p.name })) : []);
        } else {
            setRpProvinceOptions([]);
        }
        setRpComuneOptions([]);
    };

    const handleRpProvinceChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerCreateRequestField({ field: 'rpProvince', value }));
        dispatch(setCustomerCreateRequestField({ field: 'rpCity', value: null }));

        if (value) {
            const comuni = provinceComuniMap.get(value) || [];
            setRpComuneOptions(comuni.map(c => ({ value: c, label: c })));
        } else {
            setRpComuneOptions([]);
        }
    };

    const applyCapAutocomplete = (cap: string, target: 'sede' | 'rp') => {
        if (cap.length !== 5) return;

        const results = capComuneMap.get(cap);
        if (!results || results.length === 0) return;

        const first = results[0];
        const foundRegion = regions.find((r: Region) => r.name === first.region);

        const newProvinceOptions = foundRegion
            ? foundRegion.provinces.map((p: Province) => ({ value: p.code, label: p.name }))
            : [];

        const allComuniForProvince = provinceComuniMap.get(first.province) || [];
        const newComuneOptionsFull = allComuniForProvince.map((c: string) => ({ value: c, label: c }));
        const filteredComuneOptions = results.map(r => ({ value: r.comune, label: r.comune }));

        const finalComuneOptions = results.length === 1 ? newComuneOptionsFull : filteredComuneOptions;
        const finalCityValue = results.length === 1 ? first.comune : null;

        if (target === 'sede') {
            setProvinceOptions(newProvinceOptions);
            setComuneOptions(finalComuneOptions);
            setRpProvinceOptions(newProvinceOptions);
            setRpComuneOptions(finalComuneOptions);

            dispatch(setCustomerCreateRequestField({ field: 'countryRegion', value: first.region }));
            dispatch(setCustomerCreateRequestField({ field: 'province', value: first.province }));
            dispatch(setCustomerCreateRequestField({ field: 'city', value: finalCityValue }));

            dispatch(setCustomerCreateRequestField({ field: 'rpCountryRegion', value: first.region }));
            dispatch(setCustomerCreateRequestField({ field: 'rpProvince', value: first.province }));
            dispatch(setCustomerCreateRequestField({ field: 'rpCity', value: finalCityValue }));
        } else {
            setRpProvinceOptions(newProvinceOptions);
            setRpComuneOptions(finalComuneOptions);

            dispatch(setCustomerCreateRequestField({ field: 'rpCountryRegion', value: first.region }));
            dispatch(setCustomerCreateRequestField({ field: 'rpProvince', value: first.province }));
            dispatch(setCustomerCreateRequestField({ field: 'rpCity', value: finalCityValue }));
        }
    };

    const handleRadioChange = (value: string) => {
        if (value === 'SOSTITUZIONE') {
            dispatch(setCustomerCreateRequestField({ field: 'entryReason', value: 'SOSTITUZIONE' }))
            setSubentro(value);
            setPrimaNotifica(null);
            dispatch(setEntryOdc(null))
        } else if (value === 'PRIMA_NOTIFICA') {
            dispatch(setCustomerCreateRequestField({ field: 'entryReason', value: 'PRIMA_NOTIFICA' }))
            setPrimaNotifica(value);
            setSubentro(null);
            dispatch(setEntryOdc(null))
        } else if (value === 'CAMBIO_ODC_INGRESSO') {
            dispatch(setCustomerCreateRequestField({ field: 'entryReason', value: 'CAMBIO_ODC_INGRESSO' }))
            setSubentro(null);
            setPrimaNotifica(null);
        } else if (value === 'RECESSO') {
            dispatch(setCustomerCreateRequestField({ field: 'exitReason', value: 'RECESSO' }));
        } else if (value === 'CAMBIO_ODC_USCITA') {
            dispatch(setCustomerCreateRequestField({ field: 'exitReason', value: 'CAMBIO_ODC_USCITA' }));
        } else if (value === 'CAMBIO_OPERATORE') {
            dispatch(setCustomerCreateRequestField({ field: 'exitReason', value: 'CAMBIO_OPERATORE' }));
        } else if (value === 'ESCLUSIONE') {
            dispatch(setCustomerCreateRequestField({ field: 'exitReason', value: 'ESCLUSIONE' }));
        }
    };

    return (
        <div className="p-6 flex flex-col gap-6">
            <CustomAccordion
                id="panel1"
                title={<span className="text-uppercase text-white label-input">Anagrafica</span>}
                expanded={expanded === 'panel1'}
                onChange={() => handleToggleAccordion('panel1')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols gap-4'>
                    <MadfarmInput
                        required
                        label={'Ragione Sociale'}
                        placeholder="Ragione Sociale"
                        supportingText={customerState.customerCreateErrors.companyName}
                        error={!!customerState.customerCreateErrors.companyName}
                        value={customerState.customerCreateRequest.companyName || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerCreateRequestField({ field: 'companyName', value: text }))
                            dispatch(resetCustomerCreateError('companyName'))
                        }}
                    />
                </div>
                <div className='grid grid-cols-3 gap-4'>
                    <MadfarmInput
                        required
                        label={'CUAA'}
                        placeholder="CUAA"
                        supportingText={customerState.customerCreateErrors.cuaa}
                        error={!!customerState.customerCreateErrors.cuaa}
                        value={customerState.customerCreateRequest.cuaa || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerCreateRequestField({ field: 'cuaa', value: text }))
                            dispatch(resetCustomerCreateError('cuaa'))
                        }}
                    />
                    <MadfarmInput
                        required
                        label={'Partita IVA'}
                        placeholder="Partita IVA"
                        supportingText={customerState.customerCreateErrors.vat}
                        error={!!customerState.customerCreateErrors.vat}
                        value={customerState.customerCreateRequest.vat || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerCreateRequestField({ field: 'vat', value: text }))
                            dispatch(resetCustomerCreateError('vat'))
                        }}
                    />
                    <MadfarmStaticSelect
                        required
                        label='Forma Giuridica'
                        noOptionsMessage='Nessuna forma giuridica disponibile'
                        error={!!customerState.customerCreateErrors.legalForm}
                        errorLabel={customerState.customerCreateErrors.legalForm}
                        placeholder={'Forma Giuridica'}
                        value={customerState.customerCreateRequest.legalForm}
                        options={formaGiuridica}
                        onChange={(value) => {
                            //@ts-ignore
                            const newValue = value !== null ? value.value : null;
                            dispatch(setCustomerCreateRequestField({ field: 'legalForm', value: newValue }))
                            dispatch(resetCustomerCreateError('legalForm'))
                        }}
                    />
                </div>
            </CustomAccordion>
            <CustomAccordion
                id="panel2"
                title={<span className="text-uppercase text-white label-input">Sede Legale</span>}
                expanded={expanded === 'panel2'}
                onChange={() => handleToggleAccordion('panel2')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols gap-4'>
                    <MadfarmInput
                        required
                        label={'Indirizzo'}
                        placeholder="Indirizzo"
                        supportingText={customerState.customerCreateErrors.address}
                        error={!!customerState.customerCreateErrors.address}
                        value={customerState.customerCreateRequest.address || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerCreateRequestField({ field: 'address', value: text }))
                            dispatch(setCustomerCreateRequestField({ field: 'rpAddress', value: text }))
                            dispatch(resetCustomerCreateError('address'))
                        }}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmInput
                        required
                        label={'CAP'}
                        placeholder="CAP"
                        error={!!customerState.customerCreateErrors.zipCode}
                        supportingText={customerState.customerCreateErrors.zipCode}
                        value={customerState.customerCreateRequest.zipCode || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerCreateRequestField({ field: 'zipCode', value: text }));
                            dispatch(setCustomerCreateRequestField({ field: 'rpZipCode', value: text }));
                            applyCapAutocomplete(text, 'sede');
                        }}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Comune"
                        noOptionsMessage="Nessun comune trovato"
                        placeholder={'Comune'}
                        options={comuneOptions}
                        error={!!customerState.customerCreateErrors.city}
                        errorLabel={customerState.customerCreateErrors.city}
                        value={customerState.customerCreateRequest.city}
                        onChange={(value) => {
                            //@ts-ignore
                            const newValue = value !== null ? value.value : null;
                            dispatch(setCustomerCreateRequestField({ field: 'city', value: newValue }))
                            dispatch(setCustomerCreateRequestField({ field: 'rpCity', value: newValue }))
                        }}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmStaticSelect
                        required
                        label="Provincia"
                        noOptionsMessage="Nessuna provincia trovata"
                        placeholder={'Provincia'}
                        value={customerState.customerCreateRequest.province}
                        error={!!customerState.customerCreateErrors.province}
                        errorLabel={customerState.customerCreateErrors.province}
                        options={provinceOptions}
                        onChange={(val) => handleProvinceChange(val as unknown as OptionType | null)}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Regione"
                        noOptionsMessage="Nessuna regione trovata"
                        value={customerState.customerCreateRequest.countryRegion}
                        placeholder={'Regione'}
                        error={!!customerState.customerCreateErrors.countryRegion}
                        errorLabel={customerState.customerCreateErrors.countryRegion}
                        options={regionOptions}
                        onChange={(val) => handleRegionChange(val as unknown as OptionType | null)}
                    />
                </div>
            </CustomAccordion>
            <CustomAccordion
                id="panel3"
                title={<span className="text-uppercase text-white label-input">Recapito Postale</span>}
                expanded={expanded === 'panel3'}
                onChange={() => handleToggleAccordion('panel3')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols gap-4'>
                    <MadfarmInput
                        required
                        label={'Indirizzo'}
                        placeholder="Indirizzo"
                        onChangeText={(text) => dispatch(setCustomerCreateRequestField({ field: 'rpAddress', value: text }))}
                        value={customerState.customerCreateRequest.rpAddress || ''}
                        error={!!customerState.customerCreateErrors.rpAddress}
                        supportingText={customerState.customerCreateErrors.rpAddress}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmInput
                        required
                        label={'CAP'}
                        placeholder="CAP"
                        value={customerState.customerCreateRequest.rpZipCode || ''}
                        error={!!customerState.customerCreateErrors.rpZipCode}
                        supportingText={customerState.customerCreateErrors.rpZipCode}
                        onChangeText={(text) => {
                            dispatch(setCustomerCreateRequestField({ field: 'rpZipCode', value: text }));
                            applyCapAutocomplete(text, 'rp');
                        }}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Comune"
                        noOptionsMessage="Nessun comune trovato"
                        placeholder={'Comune'}
                        options={rpComuneOptions}
                        value={customerState.customerCreateRequest.rpCity}
                        error={!!customerState.customerCreateErrors.rpCity}
                        errorLabel={customerState.customerCreateErrors.rpCity}
                        onChange={(value) => {
                            //@ts-ignore
                            const newValue = value !== null ? value.value : null;
                            dispatch(setCustomerCreateRequestField({ field: 'rpCity', value: newValue }))
                        }}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmStaticSelect
                        required
                        label="Provincia"
                        noOptionsMessage="Nessuna provincia trovata"
                        placeholder={'Provincia'}
                        value={customerState.customerCreateRequest.rpProvince}
                        error={!!customerState.customerCreateErrors.rpProvince}
                        errorLabel={customerState.customerCreateErrors.rpProvince}
                        options={rpProvinceOptions}
                        onChange={(val) => handleRpProvinceChange(val as unknown as OptionType | null)}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Regione"
                        noOptionsMessage="Nessuna regione trovata"
                        value={customerState.customerCreateRequest.rpCountryRegion}
                        error={!!customerState.customerCreateErrors.rpCountryRegion}
                        errorLabel={customerState.customerCreateErrors.rpCountryRegion}
                        placeholder={'Regione'}
                        options={regionOptions}
                        onChange={(val) => handleRpRegionChange(val as unknown as OptionType | null)}
                    />
                </div>
            </CustomAccordion>

            <CustomAccordion
                id="panel4"
                title={<span className="text-uppercase text-white label-input">Dati Contabili</span>}
                expanded={expanded === 'panel4'}
                onChange={() => handleToggleAccordion('panel4')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmInput
                        label={'Codice Destinatario'}
                        placeholder="Codice Destinatario"
                        value={customerState.customerCreateRequest.addresseeCode ?? ""}
                        onChangeText={(text) => dispatch(setCustomerCreateRequestField({ field: 'addresseeCode', value: text }))}
                    />
                    <MadfarmInput
                        label={'Codice Contabile / Partitario'}
                        placeholder="Codice Contabile / Partitario"
                        value={customerState.customerCreateRequest.accountingCode ?? ""}
                        onChangeText={(text) => dispatch(setCustomerCreateRequestField({ field: 'accountingCode', value: text }))}
                    />
                </div>
                <TextArea
                    label="Coordinate Bancarie"
                    value={customerState.customerCreateRequest.bankDetails || ''}
                    onChangeText={(text) => dispatch(setCustomerCreateRequestField({ field: 'bankDetails', value: text }))}
                />
            </CustomAccordion>

            <CustomAccordion
                id="panel5"
                title={<span className="text-uppercase text-white label-input">Ingresso nel sistema di controllo</span>}
                expanded={expanded === 'panel5'}
                onChange={() => handleToggleAccordion('panel5')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols-2 gap-4'>
                    <Calendar
                        required
                        errorLabel={customerState.customerCreateErrors.startingDate}
                        label="Data Ingresso"
                        selected={customerState.customerCreateRequest.startingDate}
                        onChange={(date) => {
                            dispatch(setCustomerCreateRequestField({ field: 'startingDate', value: format(new Date(date), 'yyyy-MM-dd') }))
                        }}
                    />
                </div>
                <div className="grid grid-cols-3 gap-6">
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Prima Notifica', value: 'PRIMA_NOTIFICA' }]}
                            selectedValue={primaNotifica}
                            onChange={handleRadioChange}
                            id={'prima_notifica'}
                            name={'entry_group'}
                        />
                    </div>
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Cambio Odc', value: 'CAMBIO_ODC_INGRESSO' }]}
                            selectedValue={customerState.customerCreateRequest.entryReason === 'CAMBIO_ODC_INGRESSO' ? 'CAMBIO_ODC_INGRESSO' : null}
                            onChange={handleRadioChange}
                            id={'cambio_odc'}
                            name={'entry_group'}
                        />
                        {customerState.customerCreateRequest.entryReason === 'CAMBIO_ODC_INGRESSO' && (
                            <MadfarmStaticSelect
                                placeholder="ODC"
                                noOptionsMessage="Nessun ODC trovato"
                                options={odc}
                                value={customerState.customerCreateRequest.entryOdcId}
                                onChange={(value) => {
                                    // @ts-ignore
                                    const newValue = value !== null ? value.value : null
                                    dispatch(setCustomerCreateRequestField({ field: 'entryOdcId', value: newValue }))
                                }}
                            />
                        )}
                    </div>
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Subentro', value: 'SOSTITUZIONE' }]}
                            selectedValue={subentro}
                            onChange={handleRadioChange}
                            id={'subentro'}
                            name={'entry_group'}
                        />
                        {subentro === 'SOSTITUZIONE' && (
                            <InputMultiSearch<GetCustomerByCodeDTO>
                                searchOne
                                placeholder="Subentro"
                                onSearch={(q) =>
                                    dispatch(GetCustomersByCode(q))
                                        .unwrap()
                                        .then((res) => res.data ?? [])
                                }
                                onSelect={(id) => dispatch(setCustomerCreateRequestField({ field: 'takeoverDetails', value: id }))}
                            />
                        )}
                    </div>
                </div>
            </CustomAccordion>
        </div>
    )
}